import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/site";
import { Switch } from "@/components/ui/switch";

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [promo, setPromo] = useState({ active: false, text: "" });
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(() => {
    axios
      .get(`${API}/leads`, { withCredentials: true })
      .then((r) => setLeads(r.data.leads))
      .catch(() => {});
    axios
      .get(`${API}/settings/promo`)
      .then((r) => setPromo(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data.user))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  const savePromo = async () => {
    try {
      await axios.put(`${API}/settings/promo`, promo, { withCredentials: true });
      toast.success(promo.active ? "Offer banner is now live" : "Offer banner turned off");
    } catch {
      toast.error("Could not save offer settings");
    }
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <p className="text-sm uppercase tracking-[0.3em] text-forest/50">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-ivory px-5">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl border border-forest/10 bg-white/70 p-8 shadow-[0_30px_80px_-30px_rgba(26,54,38,0.25)] backdrop-blur-md"
        >
          <p className="font-heading text-3xl text-forest">MAHIRA Admin</p>
          <p className="mt-1 text-xs uppercase tracking-[0.3em] text-gold">
            Sign in to continue
          </p>
          <label className="mt-8 block text-xs font-semibold uppercase tracking-[0.2em] text-forest/60">
            Email
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full border-b border-forest/20 bg-transparent py-2 text-base text-charcoal outline-none transition-colors duration-300 focus:border-gold"
            />
          </label>
          <label className="mt-6 block text-xs font-semibold uppercase tracking-[0.2em] text-forest/60">
            Password
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full border-b border-forest/20 bg-transparent py-2 text-base text-charcoal outline-none transition-colors duration-300 focus:border-gold"
            />
          </label>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-700">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 w-full rounded-full bg-forest py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-forest-light disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="flex items-center justify-between bg-forest px-5 py-4 text-ivory md:px-10">
        <div>
          <p className="font-heading text-xl tracking-[0.15em]">MAHIRA</p>
          <p className="text-[9px] uppercase tracking-[0.4em] text-gold">Admin</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="admin-refresh-button"
            onClick={loadData}
            className="inline-flex items-center gap-2 rounded-full border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:bg-ivory/10"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full bg-ivory px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:bg-gold-light"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 md:px-10">
        <section
          data-testid="promo-controls"
          className="rounded-3xl border border-forest/10 bg-white/70 p-6 backdrop-blur-md"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-forest">Offer Banner</h2>
              <p className="mt-1 text-sm text-charcoal/60">
                Turn the limited-time offer strip on the website on or off.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-forest/60">
                {promo.active ? "Live" : "Off"}
              </span>
              <Switch
                data-testid="promo-toggle"
                checked={promo.active}
                onCheckedChange={(v) => setPromo((p) => ({ ...p, active: v }))}
              />
            </div>
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              data-testid="promo-text-input"
              value={promo.text}
              onChange={(e) => setPromo((p) => ({ ...p, text: e.target.value }))}
              placeholder="e.g. Free delivery across Navi Mumbai this week"
              className="flex-1 rounded-full border border-forest/15 bg-ivory px-5 py-3 text-sm outline-none transition-colors duration-300 focus:border-gold"
            />
            <button
              data-testid="promo-save-button"
              onClick={savePromo}
              className="rounded-full bg-forest px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors duration-300 hover:bg-forest-light"
            >
              Save
            </button>
          </div>
        </section>

        <section className="mt-10">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-3xl text-forest">
              Customer Enquiries
            </h2>
            <p data-testid="leads-count" className="text-sm text-charcoal/60">
              {leads.length} lead{leads.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="mt-5 overflow-x-auto rounded-3xl border border-forest/10 bg-white/70 backdrop-blur-md">
            <table data-testid="leads-table" className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-forest/10 text-[10px] uppercase tracking-[0.2em] text-forest/60">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Phone</th>
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">City</th>
                  <th className="px-5 py-4">Interest</th>
                  <th className="px-5 py-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-charcoal/50">
                      No enquiries yet. New leads from the website will appear here.
                    </td>
                  </tr>
                )}
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    className="border-b border-forest/5 last:border-0"
                  >
                    <td className="px-5 py-4 whitespace-nowrap text-charcoal/60">
                      {l.created_at
                        ? new Date(l.created_at).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                    <td className="px-5 py-4 font-medium text-forest">{l.name}</td>
                    <td className="px-5 py-4">{l.phone}</td>
                    <td className="px-5 py-4">{l.email || "—"}</td>
                    <td className="px-5 py-4">{l.city || "—"}</td>
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-gold-light/60 px-3 py-1 text-xs text-forest">
                        {l.interest}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-charcoal/70">{l.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
